"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Play, Sparkles, Star, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DynamicPricingHero() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: visualVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const avatars = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_1.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_1.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_1.webp-wvc-sizes",
    fallback: "SS",
    alt: "Hotel Manager Sunil"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_2.png-wvc-sizes",
    fallback: "RM",
    alt: "Revenue Manager Expert"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_3.png-wvc-sizes",
    fallback: "HI",
    alt: "Hospitality Investor"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_manager_4.png-wvc-sizes",
    fallback: "GM",
    alt: "General Manager"
  }];
  return <section data-section-id="118" id="dynamicpricinghero" className="bg-background text-foreground relative overflow-hidden min-h-screen flex items-center px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-lg blur-3xl opacity-40" style={{
      background: "radial-gradient(circle, hsl(222 47% 7% / 0.15) 0%, hsl(222 47% 7% / 0) 70%)"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-40 h-[36rem] w-[36rem] rounded-lg blur-3xl opacity-40" style={{
      background: "radial-gradient(circle, hsl(43 65% 35% / 0.15) 0%, hsl(43 65% 35% / 0) 70%)"
    }} />

      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
      backgroundImage: "radial-gradient(circle, hsl(222 47% 7%) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-10 items-center">
          {}
          <div ref={contentRef} className={`lg:col-span-3 flex flex-col gap-8 transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <Badge variant="outline" className="font-default border-border bg-card/40 backdrop-blur-sm px-3 py-1.5 text-xs tracking-widest uppercase w-fit gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg opacity-75" style={{
                background: "var(--secondary)"
              }} />
                <span className="relative inline-flex h-2 w-2 rounded-lg" style={{
                background: "var(--secondary)"
              }} />
              </span>
              Expert Revenue Consulting
            </Badge>

            {}
            <h1 className="font-default font-semibold tracking-tight leading-[1.05]" style={{
            fontSize: "clamp(3rem, 7vw, 6rem)"
          }}>
              <span className="block text-foreground">Maximize Your</span>
              <span className="block text-foreground">Hotel’s Revenue</span>
              <span className="block bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, hsl(222 47% 7%) 0%, hsl(43 65% 35%) 100%)"
            }}>
                With Data-Driven Strategies
              </span>
            </h1>

            {}
            <p className="text-muted-foreground text-lg sm:text-xl leading-relaxed" style={{
            maxWidth: "32ch"
          }}>
              Unlock higher occupancy and ADR with Sunil Singh’s proven OTA revenue management and tech-stack optimization.
            </p>

            {}
            <div className="flex flex-wrap items-center gap-4">
              <Button size="lg" asChild className="font-default h-12 px-7 text-base font-medium shadow-lg border-0 text-primary-foreground hover:opacity-90 transition-opacity" style={{
              background: "var(--secondary)"
            }}>
                <Link to="/ContactPage">
                  <Sparkles className="size-4" />
                  Book a Consultation
                </Link>
              </Button>
              <Button size="lg" variant="ghost" asChild className="font-default h-12 px-6 text-base font-medium text-foreground hover:bg-accent/40">
                <Link to="/OurServicesPage">
                  <Play className="size-4" />
                  View Our Services
                </Link>
              </Button>
            </div>

            {}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex -space-x-2">
                {avatars.map((avatar, i) => <SafeHydrate><Avatar key={i} data-index={i} className="size-9 ring-2 ring-background">
                    <AvatarImage src={avatar.src} data-wvc-srcset={avatar.srcset} data-wvc-sizes={avatar.sizes} alt={avatar.alt} loading="lazy" decoding="async" />
                    <AvatarFallback className="text-xs bg-secondary text-secondary-foreground">
                      {avatar.fallback}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>)}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map(i => <Star key={i} data-index={i} className="size-3.5 fill-current" style={{
                  color: "var(--secondary)"
                }} />)}
                </div>
                <span className="text-muted-foreground">
                  <span className="text-foreground font-medium">5.0/5</span>{" "}
                  Trusted by Hotel Owners
                </span>
              </div>
            </div>
          </div>

          {}
          <div ref={visualRef} className={`lg:col-span-2 relative transition-all duration-1000 delay-200 ease-out ${visualVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative" style={{
            perspective: "1500px"
          }}>
              {}
              <div className="relative" style={{
              animation: "tfwd-float 6s ease-in-out infinite"
            }}>
                {}
                <div className="relative" style={{
                transform: "rotateY(-8deg) rotateX(6deg)",
                transformStyle: "preserve-3d"
              }}>
                  <Card className="overflow-hidden border border-border bg-card shadow-4xl py-0">
                    {}
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/10">
                      <div className="flex gap-1.5">
                        <span className="size-2.5 rounded-lg bg-destructive/70" />
                        <span className="size-2.5 rounded-lg" style={{
                        background: "var(--secondary)"
                      }} />
                        <span className="size-2.5 rounded-lg" style={{
                        background: "hsl(150 70% 55% / 0.7)"
                      }} />
                      </div>
                      <div className="flex-1 mx-3">
                        <div className="h-5 rounded-md bg-background/60 border border-border flex items-center px-2.5">
                          <span className="text-[10px] text-muted-foreground font-mono truncate">
                            hotelrevbaba.com/revenue-audit
                          </span>
                        </div>
                      </div>
                    </div>
                    {}
                    <div className="aspect-[4/3] bg-background">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_dashboard_mockup.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_dashboard_mockup.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_dashboard_mockup.png-wvc-sizes" alt="Hotel revenue management dashboard showing growth metrics" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                  </Card>
                </div>

                {}
                <div className="absolute -top-6 -left-8 lg:-left-12 z-20" style={{
                animation: "tfwd-float-delayed 7s ease-in-out infinite"
              }}>
                  <Card className="px-4 py-3 bg-popover/95 backdrop-blur-md border border-border shadow-3xl flex flex-row items-center gap-3 min-w-[160px]">
                    <div className="size-9 rounded-md flex items-center justify-center shrink-0" style={{
                    background: "var(--secondary)"
                  }}>
                      <Zap className="size-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                        Revenue Lift
                      </span>
                      <span className="font-default text-lg font-semibold text-foreground leading-none">
                        +35%
                        <span className="text-xs text-muted-foreground ml-1">
                          avg
                        </span>
                      </span>
                    </div>
                  </Card>
                </div>

                {}
                <div className="absolute -bottom-8 -right-6 lg:-right-12 z-20" style={{
                animation: "tfwd-float 8s ease-in-out infinite"
              }}>
                  <Card className="px-4 py-3 bg-popover/95 backdrop-blur-md border border-border shadow-3xl flex flex-row items-center gap-2.5 min-w-[200px]">
                    <Sparkles className="size-4 shrink-0" style={{
                    color: "var(--secondary)"
                  }} />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                        Strategy
                      </span>
                      <span className="text-xs text-foreground font-mono truncate">
                        OTA Optimization...
                      </span>
                    </div>
                  </Card>
                </div>

                {}
                <div className="absolute top-1/2 -right-10 lg:-right-16 z-20 hidden sm:block" style={{
                animation: "tfwd-float-delayed 9s ease-in-out infinite"
              }}>
                  <Card className="px-3 py-2.5 bg-popover/95 backdrop-blur-md border border-border shadow-3xl flex flex-col gap-1.5">
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">
                      Focus
                    </span>
                    <div className="flex gap-1.5">
                      <span className="size-5 rounded-md" style={{
                      background: "var(--primary)"
                    }} />
                      <span className="size-5 rounded-md" style={{
                      background: "var(--secondary)"
                    }} />
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tfwd-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes tfwd-float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-16px); }
        }
      `}</style>
    </section>;
}